import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';

export default function LicensesPage() {
  const [licenses, setLicenses] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [computers, setComputers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [assigningLicense, setAssigningLicense] = useState(null);
  const [form, setForm] = useState({ licenseKey: '', licenseTypeId: '', maxActivations: 1, comment: '' });
  const [assignComputerIds, setAssignComputerIds] = useState([]);
  const [error, setError] = useState('');
  const [assignError, setAssignError] = useState('');
  const [saving, setSaving] = useState(false);

  // Search state
  const [searchText, setSearchText] = useState('');

  const fetchData = async () => {
    try {
      const [licRes, typesRes, compRes] = await Promise.all([
        api.get('/licenses'),
        api.get('/license-types'),
        api.get('/computers'),
      ]);
      setLicenses(licRes.data);
      setLicenseTypes(typesRes.data);
      setComputers(compRes.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredLicenses = useMemo(() => {
    if (!searchText.trim()) return licenses;
    const q = searchText.toLowerCase();
    return licenses.filter((lic) => {
      const keyMatch = lic.licenseKey && lic.licenseKey.toLowerCase().includes(q);
      const commentMatch = lic.comment && lic.comment.toLowerCase().includes(q);
      const typeMatch = lic.licenseType?.name?.toLowerCase().includes(q);
      const compMatch = lic.computers && lic.computers.some((c) => c.name.toLowerCase().includes(q));
      return keyMatch || commentMatch || typeMatch || compMatch;
    });
  }, [licenses, searchText]);

  const openCreate = () => {
    setEditingLicense(null);
    setForm({ licenseKey: '', licenseTypeId: '', maxActivations: 1, comment: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (lic) => {
    setEditingLicense(lic);
    setForm({
      licenseKey: lic.licenseKey || '',
      licenseTypeId: lic.licenseTypeId || '',
      maxActivations: lic.maxActivations || 1,
      comment: lic.comment || '',
    });
    setError('');
    setShowModal(true);
  };

  const openAssign = (lic) => {
    setAssigningLicense(lic);
    setAssignComputerIds(lic.computers ? lic.computers.map((c) => c.id) : []);
    setAssignError('');
    setShowAssignModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editingLicense) {
        const payload = {
          licenseKey: form.licenseKey.trim(),
          licenseTypeId: parseInt(form.licenseTypeId),
          maxActivations: parseInt(form.maxActivations) || 1,
          comment: form.comment || null,
        };
        await api.put(`/licenses/${editingLicense.id}`, payload);
      } else {
        const keys = form.licenseKey
          .split('\n')
          .map((k) => k.trim())
          .filter((k) => k.length > 0);

        if (keys.length === 0) {
          setError('Please enter at least one license key');
          setSaving(false);
          return;
        }

        await api.post('/licenses', {
          licenseKeys: keys,
          licenseTypeId: parseInt(form.licenseTypeId),
          maxActivations: parseInt(form.maxActivations) || 1,
          comment: form.comment || null,
        });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save license');
    } finally {
      setSaving(false);
    }
  };

  const toggleComputerId = (id) => {
    setAssignComputerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setAssignError('');
    try {
      await api.put(`/licenses/${assigningLicense.id}/assign`, {
        computerIds: assignComputerIds,
      });
      setShowAssignModal(false);
      fetchData();
    } catch (err) {
      setAssignError(err.response?.data?.error || 'Failed to assign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this license?')) return;
    try {
      await api.delete(`/licenses/${id}`);
      fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Get maxActivations for the assigning license key
  const assignMaxAct = assigningLicense?.maxActivations || 1;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Licenses</h1>
        <button onClick={openCreate} className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-medium rounded-lg transition-colors">
          + Add License
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 sm:flex-none sm:w-80">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by key, type, computer, comment..."
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
          {filteredLicenses.length} / {licenses.length}
        </span>
      </div>

      {/* Licenses table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        {filteredLicenses.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            {licenses.length === 0 ? 'No licenses yet. Add your first license to get started.' : 'No licenses match your filters.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                  <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">License Key</th>
                  <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="text-center px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">Max Act.</th>
                  <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Computers</th>
                  <th className="text-left px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Comment</th>
                  <th className="text-right px-4 sm:px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredLicenses.map((lic) => (
                  <tr key={lic.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-4 sm:px-6 py-4 text-sm font-mono text-gray-900 dark:text-white">
                      {lic.licenseKey ? maskKey(lic.licenseKey) : '\u2014'}
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">
                        {lic.licenseType?.name || '\u2014'}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-600 dark:text-gray-300 text-center hidden sm:table-cell">
                      {lic.maxActivations || 1}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-600 dark:text-gray-300 hidden md:table-cell">
                      {lic.computers && lic.computers.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {lic.computers.map((c) => (
                            <span key={c.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
                              {c.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate hidden lg:table-cell">{lic.comment || '\u2014'}</td>
                    <td className="px-4 sm:px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <button onClick={() => openAssign(lic)} className="p-2 sm:px-2.5 sm:py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 active:bg-indigo-100 rounded-md transition-colors" title="Assign to computers">
                          <span className="hidden sm:inline">Assign</span>
                          <svg className="w-4 h-4 sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                        </button>
                        <button onClick={() => openEdit(lic)} className="p-2 sm:px-2.5 sm:py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-md transition-colors" title="Edit">
                          <span className="hidden sm:inline">Edit</span>
                          <svg className="w-4 h-4 sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={() => handleDelete(lic.id)} className="p-2 sm:px-2.5 sm:py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 active:bg-red-100 rounded-md transition-colors" title="Delete">
                          <span className="hidden sm:inline">Delete</span>
                          <svg className="w-4 h-4 sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal title={editingLicense ? 'Edit License' : 'Add License'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave} className="space-y-4">
            {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm rounded-lg">{error}</div>}
            <div>
              {editingLicense ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">License Key *</label>
                  <input
                    type="text"
                    value={form.licenseKey}
                    onChange={(e) => setForm({ ...form, licenseKey: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm bg-white dark:bg-gray-700 dark:text-white"
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                  />
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">License Keys * (one per line for bulk add)</label>
                  <textarea
                    value={form.licenseKey}
                    onChange={(e) => setForm({ ...form, licenseKey: e.target.value })}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm font-mono bg-white dark:bg-gray-700 dark:text-white"
                    placeholder={"XXXX-XXXX-XXXX-XXXX\nYYYY-YYYY-YYYY-YYYY\nZZZZ-ZZZZ-ZZZZ-ZZZZ"}
                  />
                </>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">License Type *</label>
              <select
                value={form.licenseTypeId}
                onChange={(e) => setForm({ ...form, licenseTypeId: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select type...</option>
                {licenseTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Activations</label>
              <input
                type="number"
                min={1}
                value={form.maxActivations}
                onChange={(e) => setForm({ ...form, maxActivations: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm bg-white dark:bg-gray-700 dark:text-white"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">How many computers can this key activate</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Comment</label>
              <textarea
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm bg-white dark:bg-gray-700 dark:text-white"
                placeholder="Optional comment..."
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-sm font-medium rounded-lg transition-colors">
                {saving ? 'Saving...' : editingLicense ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Assign Modal — multi-select computers */}
      {showAssignModal && (
        <Modal title="Assign License to Computers" onClose={() => setShowAssignModal(false)}>
          <form onSubmit={handleAssign} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              License: <span className="font-mono font-medium">{maskKey(assigningLicense.licenseKey)}</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Max activations: <span className="font-semibold">{assignMaxAct}</span> &mdash; Selected: <span className={`font-semibold ${assignComputerIds.length > assignMaxAct ? 'text-red-500' : ''}`}>{assignComputerIds.length}</span>
            </p>
            {assignError && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm rounded-lg">{assignError}</div>}
            <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
              {computers.length === 0 ? (
                <div className="p-4 text-sm text-gray-400 dark:text-gray-500 text-center">No computers available</div>
              ) : (
                computers.map((c) => {
                  const checked = assignComputerIds.includes(c.id);
                  const disabled = !checked && assignComputerIds.length >= assignMaxAct;
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleComputerId(c.id)}
                        className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{c.name}</span>
                        {c.location && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{c.location}</span>}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowAssignModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={assignComputerIds.length > assignMaxAct} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-sm font-medium rounded-lg transition-colors">
                Save Assignment
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-4 sm:p-6 pb-0 bg-white dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="p-2 -mr-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 sm:p-6 pt-4">{children}</div>
      </div>
    </div>
  );
}

function maskKey(key) {
  if (!key || key.length <= 8) return key || '';
  return key.substring(0, 4) + '****' + key.substring(key.length - 4);
}
